// apps/frontend/src/lib/haptic.ts
// Haptic feedback léger pour les actions importantes mobile. Utilise
// Capacitor Haptics si l'app tourne en natif (iOS/Android), sinon
// fallback sur navigator.vibrate côté web (Android Chrome / certains
// browsers mobile). Sur iOS Safari pure web, navigator.vibrate n'existe
// pas — on no-op silencieusement, c'est OK.
//
// Usage :
//   import { haptic } from '../lib/haptic';
//   haptic.tap();      // micro-tick (favori, like)
//   haptic.success();  // confirmation (publish, send)
//   haptic.warning();  // alerte légère (validation form)
//   haptic.heavy();    // gros impact (achat, vente)

import { Capacitor } from '@capacitor/core';

type Intensity = 'light' | 'medium' | 'heavy';

interface VibratePattern {
  webMs: number | number[];
  nativeStyle: Intensity;
}

const PATTERNS: Record<string, VibratePattern> = {
  tap: { webMs: 10, nativeStyle: 'light' },
  success: { webMs: [10, 60, 10], nativeStyle: 'medium' },
  warning: { webMs: [20, 40, 20, 40], nativeStyle: 'medium' },
  heavy: { webMs: [30, 50, 30], nativeStyle: 'heavy' },
};

function isNative(): boolean {
  try {
    return Capacitor.isNativePlatform();
  } catch {
    return false;
  }
}

async function fireNative(intensity: Intensity): Promise<void> {
  try {
    // Import dynamique pour éviter d'inclure @capacitor/haptics dans le
    // bundle web si pas dispo. La dépendance n'est PAS dans package.json
    // (web-only build) — on swallow toute erreur runtime. Le
    // /* @vite-ignore */ empêche Rollup de tenter de résoudre le module
    // au build, ce qui casserait Vercel/Vite avec "Failed to resolve".
    const mod = (await import(/* @vite-ignore */ '@capacitor/haptics').catch(
      () => null
    )) as any;
    if (!mod?.Haptics?.impact) return;
    const styleMap: Record<Intensity, string> = {
      light: 'LIGHT',
      medium: 'MEDIUM',
      heavy: 'HEAVY',
    };
    await mod.Haptics.impact({ style: styleMap[intensity] });
  } catch {
    /* haptics indispo */
  }
}

function fireWeb(pattern: number | number[]): void {
  if (typeof navigator === 'undefined' || !navigator.vibrate) return;
  try {
    navigator.vibrate(pattern);
  } catch {
    /* vibrate indispo */
  }
}

function fire(kind: keyof typeof PATTERNS): void {
  if (typeof window === 'undefined') return;
  const pattern = PATTERNS[kind];
  if (!pattern) return;

  // Respect prefers-reduced-motion : pas de vibration si l'user a demandé
  // moins d'effets sensoriels.
  try {
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      return;
    }
  } catch {
    /* matchMedia indispo */
  }

  if (isNative()) {
    void fireNative(pattern.nativeStyle);
  } else {
    fireWeb(pattern.webMs);
  }
}

export const haptic = {
  /** Micro-tick — favori, like, toggle, sélection légère. */
  tap: () => fire('tap'),
  /** Confirmation positive — publish réussi, message envoyé. */
  success: () => fire('success'),
  /** Alerte légère — validation form, action ambigüe. */
  warning: () => fire('warning'),
  /** Gros impact — achat, vente, action très importante. */
  heavy: () => fire('heavy'),
};
